/**
 * AdminOrganizationPage — Organization details management.
 */

import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Edit2, Save, X } from 'lucide-react';
import { AdminConsoleService } from '../../../services/adminConsoleService';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { RequireCapability } from '../../../components/auth/RequireCapability';
import { AdminConsoleLayout } from '../components/AdminConsoleLayout';
import { ModuleCard, ModuleLoadingState } from '../../shared/components/ModulePageHeader';
import type { OrganizationDetail } from '../../../types/adminConsole';

const Field: React.FC<{
  label: string;
  value?: string;
  editing: boolean;
  name: string;
  onChange: (name: string, value: string) => void;
}> = ({ label, value, editing, name, onChange }) => (
  <div className="flex flex-col gap-1">
    <span className="enterprise-field-label">{label}</span>
    {editing ? (
      <input
        className="enterprise-input text-sm"
        value={value ?? ''}
        onChange={(e) => onChange(name, e.target.value)}
      />
    ) : (
      <span className="text-[13px] enterprise-text-primary">{value || <span className="enterprise-text-secondary italic">Não configurado</span>}</span>
    )}
  </div>
);

const planLabel: Record<string, string> = {
  starter: 'Starter',
  professional: 'Professional',
  enterprise: 'Enterprise',
};

const statusBadge: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  trial: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  suspended: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  expired: 'bg-slate-100 text-slate-600 dark:bg-zinc-800 dark:text-zinc-400',
};

export const AdminOrganizationPage: React.FC = () => {
  const { workspace } = useWorkspace();
  const orgId = workspace?.organizationId ?? '';
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Partial<OrganizationDetail>>({});

  const orgQuery = useQuery({
    queryKey: ['admin-org-detail', orgId],
    enabled: Boolean(orgId),
    queryFn: () => AdminConsoleService.getOrganizationDetail(orgId),
  });

  useEffect(() => {
    if (orgQuery.data) setDraft(orgQuery.data);
  }, [orgQuery.data]);

  const saveMutation = useMutation({
    mutationFn: () => AdminConsoleService.updateOrganization(orgId, draft),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-org-detail', orgId] });
      setEditing(false);
    },
  });

  const handleChange = (name: string, value: string) => {
    setDraft((prev) => ({ ...prev, [name]: value }));
  };

  const org = orgQuery.data;

  return (
    <RequireCapability capability="admin.access" realRoleOnly>
      <AdminConsoleLayout>
        <div className="enterprise-page h-full overflow-y-auto">
          <div className="flex items-center justify-end mb-2">
            {editing ? (
              <div className="flex gap-2">
                <button
                  type="button"
                  className="enterprise-btn-ghost flex items-center gap-1.5 text-[12px]"
                  onClick={() => { setEditing(false); if (org) setDraft(org); }}
                >
                  <X size={13} /> Cancelar
                </button>
                <button
                  type="button"
                  className="enterprise-btn-primary flex items-center gap-1.5 text-[12px]"
                  onClick={() => saveMutation.mutate()}
                  disabled={saveMutation.isPending}
                >
                  <Save size={13} /> {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="enterprise-btn-ghost flex items-center gap-1.5 text-[12px]"
                onClick={() => setEditing(true)}
              >
                <Edit2 size={13} /> Editar
              </button>
            )}
          </div>

          {orgQuery.isPending && <ModuleLoadingState label="Carregando organização" />}

          {!orgQuery.isPending && org && (
            <>
              <ModuleCard title="Dados Cadastrais">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Nome da Organização" value={draft.name} editing={editing} name="name" onChange={handleChange} />
                  <Field label="CNPJ / ID Empresa" value={draft.cnpj} editing={editing} name="cnpj" onChange={handleChange} />
                  <Field label="Setor / Indústria" value={draft.industry} editing={editing} name="industry" onChange={handleChange} />
                  <Field label="Fuso Horário" value={draft.timezone} editing={editing} name="timezone" onChange={handleChange} />
                  <Field label="E-mail de Contato" value={draft.contact_email} editing={editing} name="contact_email" onChange={handleChange} />
                  <Field label="Telefone" value={draft.phone} editing={editing} name="phone" onChange={handleChange} />
                  <div className="md:col-span-2">
                    <Field label="Endereço" value={draft.address} editing={editing} name="address" onChange={handleChange} />
                  </div>
                </div>
              </ModuleCard>

              <ModuleCard title="Plano e Licença">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-[var(--block-gap)]">
                  <div className="enterprise-card-surface p-[var(--card-padding)]">
                    <p className="enterprise-field-label">Plano</p>
                    <p className="mt-1 text-[13px] font-semibold enterprise-text-primary">
                      {planLabel[org.plan ?? ''] || org.plan || '—'}
                    </p>
                  </div>
                  <div className="enterprise-card-surface p-[var(--card-padding)]">
                    <p className="enterprise-field-label">Status</p>
                    <div className="mt-1">
                      <span className={`inline-block rounded px-2 py-0.5 text-[11px] font-medium ${statusBadge[org.status ?? ''] ?? 'bg-slate-100 text-slate-600'}`}>
                        {(org.status ?? 'N/A').toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="enterprise-card-surface p-[var(--card-padding)]">
                    <p className="enterprise-field-label">Usuários</p>
                    <p className="mt-1 text-[13px] font-semibold enterprise-text-primary tabular-nums">
                      {org.active_users ?? 0} / {org.max_users ?? '∞'}
                    </p>
                  </div>
                  <div className="enterprise-card-surface p-[var(--card-padding)]">
                    <p className="enterprise-field-label">Membro desde</p>
                    <p className="mt-1 text-[13px] enterprise-text-primary">
                      {org.created_at ? new Date(org.created_at).toLocaleDateString('pt-BR') : '—'}
                    </p>
                  </div>
                </div>
              </ModuleCard>
            </>
          )}
        </div>
      </AdminConsoleLayout>
    </RequireCapability>
  );
};
